import { useState, useEffect, useRef } from 'react';
import { useCompanyId } from '../../../../core';
import { QATConversationObjectType } from '../../../../ps-types/QATAIConversation';
import { useMessages } from '../../../../ui/MessagesProvider';
import { PSCard } from '../../../../ui/PSCard';
import { Icon, Modal } from 'semantic-ui-react';
import { QATAnalyzePropertyRisk, processDocument } from './QATAIConversation.client';



interface ScenarioRunnerProps {
  kind: 'property-risk-screening' | 'loi-generation';
  inputs: QATConversationObjectType['inputs'];
  onComplete?: () => void;
}

export function QATAIOtherAnalysisRunner({ kind, inputs, onComplete }: ScenarioRunnerProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [running, setRunning] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<string>();
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>('idle');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { error: errorMessage, clear } = useMessages();
  const resultBlockRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef<boolean>(false);

  const companyId = useCompanyId();

  // Initialize and run property risk analysis based on kind
  useEffect(() => {
    if (hasInitializedRef.current) return; // Prevent multiple initializations
    
    const runAnalysis = async () => {
      try {
        setLoading(true);
        clear();
        setAnalysisStatus('running');
        setRunning(true);
        const input = kind === 'property-risk-screening' ? inputs?.['property-risk-screening']?.['Address']: inputs?.['loi-generation']; 
        if(!input || (typeof input === 'object' && Object.keys(input).length === 0)){
          throw new Error('Input is required');
        }
        const result = kind === 'property-risk-screening' ? await QATAnalyzePropertyRisk(input as string, companyId) : await processDocument(companyId, input as QATConversationObjectType['inputs']['loi-generation']);
        setAnalysisResult(result);


        setAnalysisStatus('completed');
        onComplete?.();
      } catch (error: any) {
        console.error(`${kind} analysis error:`, error);
        errorMessage(error.message || `Failed to run ${kind}`);
        setAnalysisStatus('failed');
      } finally {
        setLoading(false);
        setRunning(false);
        hasInitializedRef.current = true;
      }
    };
    
    runAnalysis();
  }, [kind]);

  // Auto-scroll to results when they appear
  useEffect(() => {
    if ((analysisResult || running) && resultBlockRef.current) {
      resultBlockRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [analysisResult, running]);

  if (loading) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        background: 'rgba(0, 123, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(0, 123, 255, 0.2)'
      }}>
        <Icon name='spinner' loading size="large" style={{ color: '#007bff', marginBottom: '12px' }} />
        <p style={{ 
          margin: '8px 0 0 0', 
          fontSize: '16px', 
          color: '#007bff',
          fontWeight: '500'
        }}>
           {kind === 'property-risk-screening' ? 'Initializing Property Risk Analysis' : 'Initializing Letter of Intent Generation'}...
        </p>
      </div>
    );
  }

  return (
    <div ref={resultBlockRef}>
      <PSCard>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '32px',
          paddingBottom: '20px',
          borderBottom: '1px solid #f1f3f4'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '20px'
          }}>
            <Icon name='shield alternate' inverted size="large" />
          </div>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              {kind === 'property-risk-screening' ? 'Property Risk Analysis Results' : 'Letter of Intent Generation'}
            </h2>
          </div>
        </div>

        {/* Results */}
        {analysisResult && analysisStatus === 'completed' && (
          <div>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              {kind === 'property-risk-screening' ? 'Risk Analysis Report' : 'Letter of Intent'}
            </h3>
            
            {/* Unified Preview - Image for Property Risk, PDF for LOI */}
            {analysisResult && (
              <div style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                height: kind === 'loi-generation' ? '400px' : 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#007bff';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => setModalOpen(true)}
              >
                {kind === 'property-risk-screening' ? (
                  <img 
                    src={`data:image/png;base64,${analysisResult}`}
                    alt="Property Risk Analysis Report"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                ) : (
                  <iframe
                    src={`data:application/pdf;base64,${analysisResult}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block'
                    }}
                    title="Letter of Intent Preview"
                  />
                )}
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Icon name='expand' size='small' />
                  Click to enlarge
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Running State */}
        {running && analysisStatus === 'running' && (
          <div>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              {kind === 'property-risk-screening' ? 'Running Property Risk Analysis' : 'Generating Letter of Intent'}
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '16px', 
              color: '#6c757d',
              lineHeight: '1.4'
            }}>
              {kind === 'property-risk-screening' 
                ? "We're analyzing the property risk for the provided address. This process includes:"
                : "We're generating your Letter of Intent document. This process includes:"
              }
            </p>
            <ul style={{ 
              margin: '12px 0', 
              paddingLeft: '20px',
              fontSize: '14px', 
              color: '#6c757d',
              lineHeight: '1.5'
            }}>
              {kind === 'property-risk-screening' ? (
                <>
                  <li>Accessing the Unety risk assessment platform</li>
                  <li>Searching for the property address</li>
                  <li>Generating comprehensive risk report (this may take up to 1 minute)</li>
                  <li>Capturing the final report</li>
                </>
              ) : (
                <>
                  <li>Processing your project information with AI</li>
                  <li>Replacing placeholders with your specific data</li>
                  <li>Formatting the document with professional styling</li>
                  <li>Converting to PDF format</li>
                </>
              )}
            </ul>
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: 'rgba(0, 123, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 123, 255, 0.2)',
              marginTop: '16px'
            }}>
              <Icon name='spinner' loading size="large" style={{ color: '#007bff', marginBottom: '12px' }} />
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '16px', 
                color: '#007bff',
                fontWeight: '500'
              }}>
                {kind === 'property-risk-screening' ? 'Processing property risk analysis...' : 'Generating Letter of Intent...'}
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {analysisStatus === 'failed' && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            background: 'rgba(220, 53, 69, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(220, 53, 69, 0.2)'
          }}>
            <Icon name='exclamation triangle' size="large" style={{ color: '#dc3545', marginBottom: '12px' }} />
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '16px', 
              color: '#dc3545',
              fontWeight: '500'
            }}>
              {kind === 'property-risk-screening' ? 'Failed to run property risk analysis. Please try again.' : 'Failed to generate Letter of Intent. Please try again.'}
            </p>
          </div>
        )}
      </PSCard>

      {/* Unified Modal for both Image and PDF */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size={kind === 'loi-generation' ? 'fullscreen' : 'large'}
        closeIcon
        style={{ 
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important'
        }}
      >
        <Modal.Header style={{ 
          textAlign: 'center',
          fontSize: '20px',
          fontWeight: '600',
          color: '#2c3e50',
          padding: '20px 20px 10px 20px'
        }}>
          {kind === 'property-risk-screening' ? 'Property Risk Analysis Report - Full View' : 'Letter of Intent - Full View'}
        </Modal.Header>
        <Modal.Content style={{ 
          padding: '0',
          textAlign: 'center',
          background: '#f8f9fa',
          height: kind === 'loi-generation' ? '90vh' : 'auto'
        }}>
          {analysisResult && (
            <div style={{
              maxHeight: kind === 'loi-generation' ? '100%' : '80vh',
              overflow: 'auto',
              padding: '20px',
              height: kind === 'loi-generation' ? '100%' : 'auto'
            }}>
              {kind === 'property-risk-screening' ? (
                <img 
                  src={`data:image/png;base64,${analysisResult}`}
                  alt="Property Risk Analysis Report - Full View"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: '1px solid #e9ecef'
                  }}
                />
              ) : (
                <iframe
                  src={`data:application/pdf;base64,${analysisResult}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                  }}
                  title="Letter of Intent - Full View"
                />
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{ 
          textAlign: 'center',
          padding: '10px 20px 20px 20px'
        }}>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
